/**
 * Utility functions to transform Quill's flat list HTML to standard nested HTML structure.
 * 
 * Quill uses a flat list structure with CSS classes (ql-indent-1, ql-indent-2, etc.) 
 * to indicate nesting levels, which causes numbering issues when rendered outside the editor.
 * 
 * These functions convert that structure to proper nested HTML lists.
 */

/**
 * Gets the indent level from a list item's class list
 */
const getIndentLevel = (li: Element): number => {
    for (let i = 1; i <= 9; i++) {
        if (li.classList.contains(`ql-indent-${i}`)) {
            return i;
        }
    }
    return 0;
};

/**
 * Checks if HTML contains Quill indent classes
 */
export const hasQuillIndentClasses = (html: string): boolean => {
    return /ql-indent-\d/.test(html);
};

/**
 * Transforms Quill's flat list HTML to proper nested HTML structure
 * 
 * Converts:
 * <ol>
 *   <li>Item 1</li>
 *   <li class="ql-indent-1">Sub item a</li>
 *   <li class="ql-indent-1">Sub item b</li>
 *   <li>Item 2</li>
 *   <li class="ql-indent-1">Sub item a</li>
 * </ol>
 * 
 * To:
 * <ol>
 *   <li>Item 1
 *     <ol>
 *       <li>Sub item a</li>
 *       <li>Sub item b</li>
 *     </ol>
 *   </li>
 *   <li>Item 2
 *     <ol>
 *       <li>Sub item a</li>
 *     </ol>
 *   </li>
 * </ol>
 * 
 * @param html - The HTML string containing Quill's flat list structure
 * @returns The transformed HTML with proper nested lists
 */
export const transformQuillListToNested = (html: string): string => {
    if (!html || !hasQuillIndentClasses(html)) {
        return html;
    }

    // Create a temporary DOM element to parse the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Process all ordered and unordered lists
    const lists = doc.querySelectorAll('ol, ul');
    lists.forEach((list) => {
        const isOrdered = list.tagName === 'OL';
        const items = Array.from(list.children).filter(child => child.tagName === 'LI') as HTMLLIElement[];
        if (items.length === 0) return;

        // Check if any items have ql-indent classes
        const hasIndents = items.some(li => getIndentLevel(li) > 0);
        if (!hasIndents) return;

        // Build a proper nested structure
        const newList = doc.createElement(isOrdered ? 'ol' : 'ul');
        
        interface StackItem {
            listElement: HTMLOListElement | HTMLUListElement;
            indent: number;
            lastLi: HTMLLIElement | null;
        }
        
        const stack: StackItem[] = [{ listElement: newList, indent: 0, lastLi: null }];

        items.forEach((li) => {
            const indent = getIndentLevel(li);

            // Remove ql-indent classes from the li
            for (let i = 1; i <= 9; i++) {
                li.classList.remove(`ql-indent-${i}`);
            }

            // Clone the li
            const newLi = li.cloneNode(true) as HTMLLIElement;

            if (indent === 0) {
                // Root level item - add directly to the root list
                while (stack.length > 1) {
                    stack.pop();
                }
                newList.appendChild(newLi);
                stack[0].lastLi = newLi;
            } else {
                // Indented item - need to find or create proper nesting
                
                // Find the correct parent level
                while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
                    stack.pop();
                }

                const parent = stack[stack.length - 1];

                // If we need to go deeper
                if (indent > parent.indent) {
                    // We need to attach to the last li of the parent level
                    let attachTo = parent.lastLi;
                    
                    // If there's no lastLi, create a placeholder
                    if (!attachTo) {
                        attachTo = doc.createElement('li');
                        parent.listElement.appendChild(attachTo);
                        parent.lastLi = attachTo;
                    }

                    // Create nested list(s) for each level we need to go deeper
                    let currentIndent = parent.indent;
                    let currentAttach = attachTo;

                    while (currentIndent < indent) {
                        currentIndent++;
                        
                        // Check if there's already a nested list we can use
                        let nestedList = currentAttach.querySelector(':scope > ol, :scope > ul') as HTMLOListElement | HTMLUListElement | null;
                        
                        if (!nestedList) {
                            nestedList = doc.createElement(isOrdered ? 'ol' : 'ul') as HTMLOListElement | HTMLUListElement;
                            currentAttach.appendChild(nestedList);
                        }

                        if (currentIndent === indent) {
                            // This is our target level
                            nestedList.appendChild(newLi);
                            stack.push({ listElement: nestedList, indent: currentIndent, lastLi: newLi });
                        } else {
                            // Need to go deeper - create/find placeholder
                            let placeholderLi = nestedList.lastElementChild as HTMLLIElement | null;
                            if (!placeholderLi || placeholderLi.textContent?.trim()) {
                                placeholderLi = doc.createElement('li');
                                nestedList.appendChild(placeholderLi);
                            }
                            stack.push({ listElement: nestedList, indent: currentIndent, lastLi: placeholderLi });
                            currentAttach = placeholderLi;
                        }
                    }
                } else {
                    // Same level - just append
                    parent.listElement.appendChild(newLi);
                    parent.lastLi = newLi;
                }
            }
        });

        // Replace the old list with the new nested one
        list.parentNode?.replaceChild(newList, list);
    });

    return doc.body.innerHTML;
};

/**
 * CSS styles for properly rendered nested lists.
 * Use this with MUI's sx prop or as a CSS string.
 * 
 * Pattern repeats every 3 levels:
 * Level 1: decimal (1, 2, 3)
 * Level 2: lower-alpha (a, b, c)
 * Level 3: lower-roman (i, ii, iii)
 * Level 4: decimal (1, 2, 3) - repeats
 * Level 5: lower-alpha (a, b, c) - repeats
 * Level 6: lower-roman (i, ii, iii) - repeats
 */
export const nestedListStyles = {
    '& ol': {
        listStyleType: 'decimal !important',
        paddingLeft: '20px',
        marginTop: '4px',
        marginBottom: '4px',
    },
    '& ol ol': {
        listStyleType: 'lower-alpha !important',
    },
    '& ol ol ol': {
        listStyleType: 'lower-roman !important',
    },
    '& ol ol ol ol': {
        listStyleType: 'decimal !important',
    },
    '& ol ol ol ol ol': {
        listStyleType: 'lower-alpha !important',
    },
    '& ol ol ol ol ol ol': {
        listStyleType: 'lower-roman !important',
    },
    '& ol ol ol ol ol ol ol': {
        listStyleType: 'decimal !important',
    },
    '& ul': {
        listStyleType: 'disc !important',
        paddingLeft: '20px',
        marginTop: '4px',
        marginBottom: '4px',
    },
    '& ul ul': {
        listStyleType: 'circle !important',
    },
    '& ul ul ul': {
        listStyleType: 'square !important',
    },
    '& ul ul ul ul': {
        listStyleType: 'disc !important',
    },
    '& ul ul ul ul ul': {
        listStyleType: 'circle !important',
    },
    '& ul ul ul ul ul ul': {
        listStyleType: 'square !important',
    },
};

/**
 * CSS string version of nested list styles for non-MUI usage
 * 
 * Pattern repeats every 3 levels:
 * Level 1: decimal (1, 2, 3)
 * Level 2: lower-alpha (a, b, c)
 * Level 3: lower-roman (i, ii, iii)
 * Level 4+: repeats the pattern
 */
export const nestedListStylesCSS = `
ol { list-style-type: decimal !important; padding-left: 20px; margin-top: 4px; margin-bottom: 4px; }
ol ol { list-style-type: lower-alpha !important; }
ol ol ol { list-style-type: lower-roman !important; }
ol ol ol ol { list-style-type: decimal !important; }
ol ol ol ol ol { list-style-type: lower-alpha !important; }
ol ol ol ol ol ol { list-style-type: lower-roman !important; }
ol ol ol ol ol ol ol { list-style-type: decimal; }
ul { list-style-type: disc; padding-left: 20px; margin-top: 4px; margin-bottom: 4px; }
ul ul { list-style-type: circle; }
ul ul ul { list-style-type: square; }
ul ul ul ul { list-style-type: disc; }
ul ul ul ul ul { list-style-type: circle; }
ul ul ul ul ul ul { list-style-type: square; }
`;
